import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { plural } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { AlertTriangle, ArrowLeftRight, Download, FileSpreadsheet, Loader2, Upload } from "lucide-react";
import { toast } from "sonner";

/**
 * Employees from a spreadsheet: choose → check → import.
 *
 * Shakun HR's first attempt (14 Sep 2026) uploaded our own employee export and was refused
 * ten times by a toast reading "Missing required column: employee_code" — a name that was in
 * neither their file nor this dialog, which listed columns in its description as snake_case.
 * The server now reads the headers people actually write, and this dialog shows what it
 * understood BEFORE anything is saved: which sheet and columns it read, how many people are
 * ready, what needs fixing by row number, and the two decisions a real file needs (a column
 * swap it noticed, units and departments Masters does not have yet). Nothing is written until
 * every row passes; the preview is what makes that rule cheap to live with.
 *
 * The column guide comes from the server, which is the same list the import reads — a guide
 * written here would be a fourth copy of a vocabulary that already drifted with three.
 */
export default function ImportEmployeesDialog({ open, onOpenChange, onImported }) {
  const [columns, setColumns] = useState(null);
  const [file, setFile] = useState(null);
  const [report, setReport] = useState(null);
  const [refusal, setRefusal] = useState("");
  const [checking, setChecking] = useState(false);
  const [importing, setImporting] = useState(false);
  const [swap, setSwap] = useState(false);
  const [create, setCreate] = useState(false);
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef(null);
  const seq = useRef(0);

  useEffect(() => {
    if (!open || columns) return;
    api.get("/employees/import/columns")
      .then((r) => Array.isArray(r.data) && setColumns(r.data))
      .catch(() => setColumns([]));
  }, [open, columns]);

  const reset = () => {
    seq.current++;
    setFile(null); setReport(null); setRefusal(""); setSwap(false); setCreate(false);
    setChecking(false);
  };
  const close = (v) => { if (!v && !importing) { reset(); onOpenChange(false); } };

  // Numbered: flipping a switch while the previous check is still out must not let the
  // older answer land over the newer one and show counts for the other setting.
  const check = async (f, opts) => {
    const n = ++seq.current;
    setChecking(true);
    try {
      const r = await api.post("/employees/import/preview", form(f, opts));
      if (n !== seq.current) return;
      setReport(r.data); setRefusal("");
    } catch (e) {
      if (n !== seq.current) return;
      setReport(null);
      setRefusal(formatApiError(e.response?.data?.detail, "The file could not be checked."));
    } finally {
      if (n === seq.current) setChecking(false);
    }
  };

  const choose = (f) => {
    if (!f) return;
    reset();
    setFile(f);
    check(f, { swap: false, create: false });
  };

  const toggle = (next) => {
    const opts = { swap, create, ...next };
    setSwap(opts.swap); setCreate(opts.create);
    check(file, opts);
  };

  const downloadTemplate = async () => {
    try {
      const r = await api.get("/employees/import-template.xlsx", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = "employees-import-template.xlsx"; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      toast.error("Could not download the template");
    }
  };

  const runImport = async () => {
    setImporting(true);
    try {
      const r = await api.post("/employees/bulk-import", form(file, { swap, create }));
      const made = [r.data.units_created && plural(r.data.units_created, "unit"),
                    r.data.departments_created && plural(r.data.departments_created, "department")].filter(Boolean);
      toast.success(`Imported ${plural(r.data.created, "employee")}` + (made.length ? ` · created ${made.join(" and ")}` : ""));
      reset();
      onOpenChange(false);
      onImported?.();
    } catch (e) {
      // Somebody else changed the register between the check and the import, most likely.
      // The refusal carries a fresh report, so the screen shows the new truth, not a toast.
      if (e.response?.data?.report) setReport(e.response.data.report);
      toast.error(formatApiError(e.response?.data?.detail, "Import failed"));
    } finally {
      setImporting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-2xl max-h-[90dvh] overflow-y-auto" data-testid="import-dialog">
        <DialogHeader>
          <DialogTitle>Import employees from Excel</DialogTitle>
          <DialogDescription>
            Fill in the template, or upload an Employees export. You will see what the file
            contains before anything is saved.
          </DialogDescription>
        </DialogHeader>

        <input ref={inputRef} type="file" accept=".xlsx" className="hidden" data-testid="import-file-input"
               onChange={(e) => { choose(e.target.files?.[0]); e.target.value = ""; }} />

        {!file ? (
          <div className="space-y-4 min-w-0">
            <button type="button" onClick={() => inputRef.current?.click()}
                    onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                    onDragLeave={() => setDragging(false)}
                    onDrop={(e) => { e.preventDefault(); setDragging(false); choose(e.dataTransfer.files?.[0]); }}
                    className={`w-full flex flex-col items-center gap-2 rounded-md border-2 border-dashed p-8 transition-colors ${dragging ? "border-primary bg-primary/5" : "border-border hover:bg-muted/50"}`}
                    data-testid="import-drop">
              <Upload className="w-6 h-6 text-primary" />
              <span className="text-sm font-medium">Choose an .xlsx file, or drop it here</span>
              <span className="text-xs text-muted-foreground">Nothing is imported until you confirm</span>
            </button>
            <ColumnGuide columns={columns} onTemplate={downloadTemplate} />
          </div>
        ) : (
          <div className="space-y-4 min-w-0" data-testid="import-check">
            <div className="flex items-center justify-between gap-3 rounded-md border border-border px-3 py-2">
              <div className="flex items-center gap-2 min-w-0">
                <FileSpreadsheet className="w-4 h-4 shrink-0 text-emerald-600 dark:text-emerald-400" />
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{file.name}</div>
                  {report && (
                    <div className="text-xs text-muted-foreground">
                      Sheet “{report.sheet}”, headers on row {report.header_row} · {plural(report.total_rows, "person", "people")}
                    </div>
                  )}
                </div>
              </div>
              <Button variant="ghost" size="sm" onClick={() => inputRef.current?.click()} disabled={importing}
                      data-testid="import-choose-another">Choose another</Button>
            </div>

            {checking && !report && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground py-6 justify-center">
                <Loader2 className="w-4 h-4 animate-spin" /> Reading the file…
              </div>
            )}

            {refusal && (
              <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm" data-testid="import-refusal">
                <div className="font-medium text-destructive-text">This file cannot be imported as it is</div>
                <p className="mt-1">{refusal}</p>
                <Button variant="link" className="h-auto p-0 mt-2" onClick={downloadTemplate}>
                  <Download className="w-3.5 h-3.5 mr-1" /> Download the template
                </Button>
              </div>
            )}

            {report && <Report report={report} swap={swap} create={create} busy={checking || importing} onToggle={toggle} />}
          </div>
        )}

        {/* Above the buttons, not inside the footer: the footer stacks in reverse on a phone,
            which put the reason Import is disabled underneath the button it explains. */}
        {report && !report.can_import && (
          <p className="text-sm text-muted-foreground sm:text-right -mb-2" data-testid="import-blocked">
            {report.blocked_by.join(". ")}.
          </p>
        )}
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => close(false)} disabled={importing}>Cancel</Button>
          {file && (
            <Button onClick={runImport} disabled={!report?.can_import || checking || importing} data-testid="import-confirm">
              {importing && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}
              {report?.can_import ? `Import ${plural(report.ready_count, "employee")}` : "Import"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function form(file, { swap, create }) {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("swap_columns", swap ? "true" : "false");
  fd.append("create_missing", create ? "true" : "false");
  return fd;
}

function ColumnGuide({ columns, onTemplate }) {
  return (
    <div className="rounded-md border border-border overflow-hidden">
      <div className="flex items-center justify-between gap-2 px-3 py-2 bg-muted/40 border-b border-border">
        <span className="overline text-muted-foreground">Columns this import reads</span>
        <Button variant="link" size="sm" className="h-auto p-0" onClick={onTemplate} data-testid="download-template">
          <Download className="w-3.5 h-3.5 mr-1" /> Download template
        </Button>
      </div>
      {columns === null ? (
        <div className="p-3 text-xs text-muted-foreground">Loading…</div>
      ) : (
        <ul className="divide-y divide-border text-sm">
          {columns.map((c) => (
            <li key={c.key} className="px-3 py-1.5 grid grid-cols-[minmax(0,9rem)_minmax(0,1fr)] gap-x-3">
              <span className="font-medium">
                {c.label}{c.required && <span className="text-destructive-text ml-0.5" aria-label="required">*</span>}
              </span>
              <span className="text-muted-foreground min-w-0">
                <span className="font-mono text-xs">{c.example}</span>
                {c.note && <span className="block text-xs">{c.note}</span>}
              </span>
            </li>
          ))}
        </ul>
      )}
      <p className="px-3 py-1.5 text-xs text-muted-foreground border-t border-border">
        <span className="text-destructive-text">*</span> Required. Headers can be in any order and any
        case; “Code”, “Name” and “DOJ” from an export work too.
      </p>
    </div>
  );
}

function Report({ report, swap, create, busy, onToggle }) {
  const missing = report.missing_masters;
  const needsMasters = missing.units.length > 0 || missing.departments.length > 0;
  const renamed = report.matched.filter((m) => m.header && m.header.toLowerCase() !== m.label.toLowerCase());
  const waiting = create ? 0 : missing.rows;

  return (
    <div className={`space-y-4 transition-opacity ${busy ? "opacity-60" : ""}`}>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="import-counts">
        <Count n={report.ready_count} label="ready to import" tone="text-emerald-700 dark:text-emerald-400" />
        <Count n={report.already_on_file.length} label="already on the register" />
        <Count n={report.problems.length} label={report.problems.length === 1 ? "row needs fixing" : "rows need fixing"}
               tone={report.problems.length ? "text-destructive-text" : ""} />
        <Count n={waiting} label="waiting on Masters" tone={waiting ? "text-amber-700 dark:text-amber-400" : ""} />
      </div>

      {renamed.length > 0 && (
        <p className="text-xs text-muted-foreground">
          Read {renamed.map((m) => `“${m.header}” as ${m.label}`).join(", ")}.
        </p>
      )}
      {report.duplicate_columns?.map((d) => (
        <p key={d.column} className="text-xs text-amber-700 dark:text-amber-400" data-testid="import-duplicate-column">
          Column {d.column} (“{d.header}”) is a second {d.label} column — only column {d.read} is read.
        </p>
      ))}
      {report.ignored_columns.length > 0 && (
        <p className="text-xs text-muted-foreground">
          Not imported (nothing to put them in): {report.ignored_columns.join(", ")}.
        </p>
      )}

      {report.swap.suspected && (
        <Decision icon={ArrowLeftRight} testid="import-swap"
                  title={swap ? "Designation and Department are swapped for this import" : "Designation and Department look swapped"}
                  body={<>
                    Department holds {quoteList(report.swap.department_values)} — job titles — while Designation
                    holds {quoteList(report.swap.designation_values)}. Your file is not changed either way.
                  </>}
                  control={<Switch checked={swap} onCheckedChange={(v) => onToggle({ swap: v })} disabled={busy}
                                   aria-label="Swap Designation and Department" />}
                  controlLabel="Swap them" />
      )}

      {needsMasters && (
        <Decision icon={AlertTriangle} testid="import-masters"
                  title={`${[missing.units.length && plural(missing.units.length, "unit"),
                             missing.departments.length && plural(missing.departments.length, "department")]
                            .filter(Boolean).join(" and ")} not in Masters yet`}
                  body={<MastersList missing={missing} />}
                  control={<Checkbox checked={create} onCheckedChange={(v) => onToggle({ create: v === true })}
                                     disabled={busy} aria-label="Create them during this import" />}
                  controlLabel="Create them during this import"
                  footnote={report.swap.suspected && !swap
                    ? "Decide the swap above first — as the file stands, these job titles would become departments."
                    : "Check the spelling first: each name becomes a real unit or department, just as if it were added in Masters."} />
      )}

      {report.problems.length > 0 && (
        <div className="rounded-md border border-destructive/40" data-testid="import-problems">
          <div className="px-3 py-2 border-b border-destructive/30 text-sm font-medium text-destructive-text">
            Fix {report.problems.length === 1 ? "this row" : "these rows"} in the file, save it, and choose it again
          </div>
          <ul className="max-h-48 overflow-y-auto divide-y divide-border text-sm">
            {report.problems.map((p) => (
              <li key={p.row} className="px-3 py-1.5">
                <span className="font-mono text-xs text-muted-foreground mr-2">Row {p.row}</span>
                {p.label && <span className="font-medium mr-1">{p.label}:</span>}
                {p.issues.map((i) => i.message).join("; ")}
              </li>
            ))}
          </ul>
        </div>
      )}

      {report.ready_sample.length > 0 && (
        <div className="rounded-md border border-border" data-testid="import-sample">
          <div className="px-3 py-2 border-b border-border text-xs text-muted-foreground">
            How they will be added{report.ready_count > report.ready_sample.length &&
              ` — the first ${report.ready_sample.length} of ${report.ready_count}`}
          </div>
          {/* Five columns, not seven. Seven did not fit the dialog, and a plant name like
              HALOL-3-SEMICON-STAFF pushed Joined — the column most worth checking, since dates
              are read day first — out of sight. The code rides under the name and the unit
              under the department, where each is read together anyway. */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs text-muted-foreground">
                <tr className="text-left">
                  {/* On a phone Row and Designation fold away (Designation rides under the name),
                      so the three columns left — who, where, joined — fit 390px without the
                      joining date scrolling out of the table, which it did on HR's own file. */}
                  {[["Row", "hidden sm:table-cell"], ["Person", ""], ["Designation", "hidden sm:table-cell"],
                    ["Department · Unit", ""], ["Joined", ""]].map(([h, cls]) => (
                    <th key={h} className={`px-3 py-1.5 font-medium whitespace-nowrap ${cls}`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {report.ready_sample.map((r) => (
                  <tr key={r.row} className="align-top">
                    <td className="px-3 py-1.5 font-mono text-xs text-muted-foreground hidden sm:table-cell">{r.row}</td>
                    <td className="px-3 py-1.5">
                      <div>{r.full_name}</div>
                      <div className="text-xs text-muted-foreground">
                        <span className="font-mono">{r.employee_code}</span>
                        {r.designation && <span className="sm:hidden"> · {r.designation}</span>}
                      </div>
                    </td>
                    <td className="px-3 py-1.5 hidden sm:table-cell">{r.designation || "—"}</td>
                    <td className="px-3 py-1.5">
                      <div>{r.department}</div>
                      <div className="text-xs text-muted-foreground break-words">{r.unit}</div>
                    </td>
                    <td className="px-3 py-1.5 whitespace-nowrap">{r.date_of_joining || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {report.already_on_file.length > 0 && (
        <p className="text-xs text-muted-foreground" data-testid="import-already">
          Left as they are, already on the register: {report.already_on_file.slice(0, 8).map((a) => a.employee_code).join(", ")}
          {report.already_on_file.length > 8 && ` and ${report.already_on_file.length - 8} more`}.
          Import only adds people; it never changes anybody.
        </p>
      )}
    </div>
  );
}

function Count({ n, label, tone = "" }) {
  return (
    <div className="rounded-md border border-border px-3 py-2">
      <div className={`font-heading text-xl font-bold ${n ? tone : "text-muted-foreground"}`}>{n}</div>
      <div className="text-xs text-muted-foreground leading-tight">{label}</div>
    </div>
  );
}

function Decision({ icon: Icon, title, body, control, controlLabel, footnote, testid }) {
  return (
    <div className="rounded-md border border-amber-500/40 bg-amber-500/5 p-3 space-y-2" data-testid={testid}>
      <div className="flex gap-2">
        <Icon className="w-4 h-4 mt-0.5 shrink-0 text-amber-700 dark:text-amber-400" />
        <div className="min-w-0 space-y-1">
          <div className="text-sm font-medium">{title}</div>
          <div className="text-sm text-muted-foreground">{body}</div>
        </div>
      </div>
      <label className="flex items-center gap-2 pl-6 text-sm font-medium cursor-pointer w-fit">
        {control} {controlLabel}
      </label>
      {footnote && <p className="pl-6 text-xs text-muted-foreground">{footnote}</p>}
    </div>
  );
}

function MastersList({ missing }) {
  const byUnit = {};
  missing.departments.forEach((d) => { (byUnit[d.unit] ||= []).push(d.name); });
  missing.units.forEach((u) => { byUnit[u] ||= []; });
  return (
    <ul className="mt-1 space-y-0.5">
      {Object.entries(byUnit).map(([unit, names]) => (
        <li key={unit}>
          <span className="text-foreground">{unit}</span>
          {missing.units.includes(unit) && <span className="text-xs"> (new unit)</span>}
          {names.length > 0 && <>: {names.join(", ")}</>}
        </li>
      ))}
    </ul>
  );
}

const quoteList = (values) => values.map((v) => `“${v}”`).join(", ");
